'use client';

import React from 'react';
import { ShieldCheck, Clock, MapPin, Headphones, Car, CreditCard, Sparkles, Check } from 'lucide-react';
import type { SectionProps } from '@/lib/storefront/section-templates';
import { containerClass, sectionStyle } from './HeaderSections';

const ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  shield: ShieldCheck,
  clock: Clock,
  map: MapPin,
  headset: Headphones,
  car: Car,
  card: CreditCard,
  sparkles: Sparkles,
};

const DEFAULT_FEATURES = [
  { icon: 'shield', title: 'Full insurance included', text: 'Every rental ships with comprehensive cover and a zero-excess option.' },
  { icon: 'clock', title: 'Flexible pick-up', text: 'Collect at the airport, your hotel, or our downtown office — any hour.' },
  { icon: 'card', title: 'No hidden fees', text: 'The price you see at checkout is the price you pay at the counter.' },
  { icon: 'headset', title: '24/7 assistance', text: 'A real person answers the phone, day or night, wherever you are.' },
];

function IconBadge({ name }: { name: string }) {
  const Icon = ICONS[name] ?? Sparkles;
  return (
    <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand/10 text-brand">
      <Icon className="h-5 w-5" />
    </span>
  );
}

// ──────────────────────────────────────────────────────────────
// 1. Grid of feature cards
// ──────────────────────────────────────────────────────────────

export function FeaturesGrid({ content, style }: SectionProps) {
  const {
    eyebrow = 'Why book with us',
    headline = 'Everything you need for the road',
    subheadline = 'Simple pricing, a well-maintained fleet and support that actually answers.',
    columns = 4,
    items = DEFAULT_FEATURES,
  } = content;

  const columnCount = Number(columns) || 4;
  const cols =
    columnCount === 2 ? 'sm:grid-cols-2' : columnCount === 3 ? 'sm:grid-cols-2 lg:grid-cols-3' : 'sm:grid-cols-2 lg:grid-cols-4';

  return (
    <section className="w-full bg-white" style={sectionStyle(style)}>
      <div className={containerClass(style)}>
        <div className="mx-auto max-w-2xl text-center">
          <span className="text-xs font-semibold uppercase tracking-wider text-brand">{eyebrow}</span>
          <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">{headline}</h2>
          {subheadline && <p className="mt-3 text-base text-slate-600">{subheadline}</p>}
        </div>

        <div className={`mt-10 grid gap-5 ${cols}`}>
          {items.map((item: any, i: number) => (
            <div key={i} className="rounded-2xl border border-slate-200 bg-white p-6 transition-shadow hover:shadow-md">
              <IconBadge name={item.icon} />
              <h3 className="mt-4 text-base font-semibold text-slate-900">{item.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{item.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ──────────────────────────────────────────────────────────────
// 2. Alternating rows with image
// ──────────────────────────────────────────────────────────────

export function FeaturesAlternating({ content, style }: SectionProps) {
  const {
    headline = 'Built around your trip',
    subheadline = 'Three things our guests tell us made the difference.',
    rows = [
      {
        title: 'Book in under two minutes',
        text: 'Pick your dates, choose a car, pay online. Your keys are waiting when you land.',
        imageUrl: 'https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?auto=format&fit=crop&w=1200&q=80',
      },
      {
        title: 'Cars you can trust',
        text: 'Every vehicle is serviced between rentals and fully cleaned before pick-up.',
        imageUrl: 'https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?auto=format&fit=crop&w=1200&q=80',
      },
      {
        title: 'Return without the fuss',
        text: 'Drop the keys, we handle the rest. No queue, no surprise inspection fees.',
        imageUrl: 'https://images.unsplash.com/photo-1502877338535-766e1452684a?auto=format&fit=crop&w=1200&q=80',
      },
    ],
  } = content;

  return (
    <section className="w-full bg-white" style={sectionStyle(style)}>
      <div className={containerClass(style)}>
        <div className="max-w-2xl">
          <h2 className="text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">{headline}</h2>
          {subheadline && <p className="mt-3 text-base text-slate-600">{subheadline}</p>}
        </div>

        <div className="mt-10 space-y-12">
          {rows.map((row: any, i: number) => (
            <div key={i} className={`grid items-center gap-8 lg:grid-cols-2 ${i % 2 === 1 ? 'lg:[&>div:first-child]:order-2' : ''}`}>
              <div>
                <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-brand text-sm font-bold text-white">
                  {i + 1}
                </span>
                <h3 className="mt-4 text-2xl font-semibold text-slate-900">{row.title}</h3>
                <p className="mt-3 text-base leading-relaxed text-slate-600">{row.text}</p>
              </div>
              <div>
                <img src={row.imageUrl} alt={row.title ?? ''} className="aspect-[16/10] w-full rounded-2xl object-cover shadow-lg" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ──────────────────────────────────────────────────────────────
// 3. Checklist + stats band
// ──────────────────────────────────────────────────────────────

export function FeaturesChecklist({ content, style }: SectionProps) {
  const {
    headline = 'The practical stuff, handled',
    subheadline = 'No fine print surprises. Here is exactly what comes with your booking.',
    items = [
      'Comprehensive insurance with a 500 DH excess',
      'Unlimited kilometres on rentals over 3 days',
      'Free cancellation up to 24 hours before pick-up',
      'Additional driver included at no cost',
      'Child seat available on request',
      'Delivery and collection across Agadir',
    ],
    stats = [
      { value: '120+', label: 'Cars in the fleet' },
      { value: '12k', label: 'Happy guests' },
      { value: '4.9', label: 'Average rating' },
      { value: '8 yrs', label: 'On the road' },
    ],
  } = content;

  return (
    <section className="w-full bg-slate-50" style={sectionStyle(style)}>
      <div className={`${containerClass(style)} grid gap-10 lg:grid-cols-2`}>
        <div>
          <h2 className="text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">{headline}</h2>
          {subheadline && <p className="mt-3 text-base text-slate-600">{subheadline}</p>}

          <ul className="mt-7 grid gap-3 sm:grid-cols-2">
            {items.map((item: string, i: number) => (
              <li key={i} className="flex items-start gap-2.5 rounded-xl bg-white p-3 text-sm text-slate-700 shadow-sm">
                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
                  <Check className="h-3.5 w-3.5" />
                </span>
                {item}
              </li>
            ))}
          </ul>
        </div>

        <div className="grid grid-cols-2 gap-4 self-center">
          {stats.map((s: any, i: number) => (
            <div key={i} className="rounded-2xl border border-slate-200 bg-white p-6 text-center">
              <p className="text-3xl font-bold text-slate-900">{s.value}</p>
              <p className="mt-1 text-xs font-medium uppercase tracking-wide text-slate-500">{s.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export const FEATURE_VARIATIONS = {
  grid: FeaturesGrid,
  alternating: FeaturesAlternating,
  checklist: FeaturesChecklist,
} as const;

export type FeatureVariationKey = keyof typeof FEATURE_VARIATIONS;
